import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Percent, Gift, Power } from "lucide-react";
import { api, inr, apiError } from "@/lib/api";
import { Skeleton } from "@/components/common";

const empty = { name: "", type: "PERCENT", value: "", max_cap: "", min_amount: "", payment_type: "ALL", lender: "", start_date: "", end_date: "", active: true };
const inputCls = "mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#14B8A6]";

export default function RewardRules() {
  const [rules, setRules] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/reward-rules").then((r) => setRules(r.data)).catch(() => setRules([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim() || !form.value) { toast.error("Name and reward value are required."); return; }
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(), type: form.type, value: Number(form.value),
        max_cap: form.max_cap ? Number(form.max_cap) : null,
        min_amount: form.min_amount ? Number(form.min_amount) : null,
        payment_type: form.payment_type, lender: form.lender.trim() || null,
        start_date: form.start_date || null, end_date: form.end_date || null, active: form.active,
      };
      await api.post("/admin/reward-rules", body);
      toast.success("Reward rule created.");
      setForm(empty); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const toggle = async (r) => {
    try { await api.patch(`/admin/reward-rules/${r.id}`, { active: !r.active }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const desc = (r) => r.type === "FIXED" ? `₹${r.value} fixed` : `${r.value}%${r.max_cap ? ` (max ${inr(r.max_cap)})` : ""}`;

  return (
    <div className="admin-console space-y-5" data-testid="reward-rules-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-[#0B132B]">Reward Rules</h1>
        <p className="text-sm text-slate-500">Configure wallet rewards without code changes. Rewards accrue as pending and are credited only after lender confirmation.</p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="flex items-center gap-2 font-display font-bold text-[#0B132B]"><Plus size={16} /> New Reward Rule</h3>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div><label className="text-xs font-medium text-slate-500">Name</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="EMI 1% (max ₹100)" data-testid="rr-name" /></div>
          <div><label className="text-xs font-medium text-slate-500">Type</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls} data-testid="rr-type"><option value="PERCENT">Percentage</option><option value="FIXED">Fixed ₹</option></select></div>
          <div><label className="text-xs font-medium text-slate-500">{form.type === "FIXED" ? "Amount (₹)" : "Percentage (%)"}</label><input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className={inputCls} data-testid="rr-value" /></div>
          <div><label className="text-xs font-medium text-slate-500">Max cap (₹)</label><input type="number" value={form.max_cap} onChange={(e) => setForm({ ...form, max_cap: e.target.value })} className={inputCls} placeholder="optional" data-testid="rr-cap" /></div>
          <div><label className="text-xs font-medium text-slate-500">Min payment (₹)</label><input type="number" value={form.min_amount} onChange={(e) => setForm({ ...form, min_amount: e.target.value })} className={inputCls} placeholder="optional" data-testid="rr-min" /></div>
          <div><label className="text-xs font-medium text-slate-500">Payment type</label>
            <select value={form.payment_type} onChange={(e) => setForm({ ...form, payment_type: e.target.value })} className={inputCls} data-testid="rr-ptype">{["ALL", "EMI", "OVERDUE", "POS", "CUSTOM"].map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
          <div><label className="text-xs font-medium text-slate-500">Lender (optional)</label><input value={form.lender} onChange={(e) => setForm({ ...form, lender: e.target.value })} className={inputCls} placeholder="All lenders" data-testid="rr-lender" /></div>
          <div className="grid grid-cols-2 gap-2"><div><label className="text-xs font-medium text-slate-500">Start</label><input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={inputCls} /></div><div><label className="text-xs font-medium text-slate-500">End</label><input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={inputCls} /></div></div>
        </div>
        <button onClick={create} disabled={busy} data-testid="rr-create" className="mt-4 rounded-md bg-[#14B8A6] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0D9488] disabled:opacity-50">{busy ? "Saving…" : "Create Rule"}</button>
        <p className="mt-2 text-xs text-slate-400">Percentages cannot exceed 100%. Set a max cap to protect margin — admin margin (Gross − Discount − Reward Cost) is visible in the Collection Command Center.</p>
      </div>

      {!rules ? <Skeleton className="h-48" /> : rules.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">No reward rules yet. Create one above.</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {rules.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`rr-card-${r.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#14B8A6]/10 text-[#14B8A6]">{r.type === "FIXED" ? <Gift size={16} /> : <Percent size={16} />}</span>
                  <div><p className="font-semibold text-[#0B132B]">{r.name}</p><p className="text-xs text-slate-400">{desc(r)}</p></div>
                </div>
                <button onClick={() => toggle(r)} data-testid={`rr-toggle-${r.id}`} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${r.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}><Power size={11} /> {r.active ? "Active" : "Inactive"}</button>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
                <span className="rounded bg-slate-100 px-2 py-0.5 font-semibold text-slate-600">{r.payment_type}</span>
                {r.lender && <span className="rounded bg-slate-100 px-2 py-0.5 text-slate-600">{r.lender}</span>}
                {r.min_amount ? <span className="rounded bg-slate-100 px-2 py-0.5 text-slate-600">min {inr(r.min_amount)}</span> : null}
                {r.is_demo && <span className="rounded bg-amber-100 px-2 py-0.5 font-bold text-amber-700">DEMO</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
